import { useEffect, useId, useSyncExternalStore } from "react";

import { cn, platformProps } from "../../lib";
import { Box } from "../../primitives";
import { useOptionalScreenLayoutStore, useScreenLayoutStore } from "./context";
import { ScreenLayoutKeyboardSticky } from "./screen-layout-keyboard-sticky";
import { screenLayoutActionsVariants } from "./screen-layout.variants";
import type { ScreenLayoutActionsContentProps, ScreenLayoutActionsPortalProps } from "./types";

/**
 * ScreenLayout.ActionsPortal — the host. Subscribes to the store and
 * renders the latest entry registered by any `<ActionsContent>` in the
 * screen subtree. When a direct `<Actions>` is also mounted, renders
 * nothing (direct child wins); the conflict warning is raised by the
 * root's warnings probe.
 */
export const ScreenLayoutActionsPortal = ({
  surface = "shadow",
  roundedTop = false,
  className,
  testID,
}: ScreenLayoutActionsPortalProps) => {
  const store = useScreenLayoutStore("ScreenLayout.ActionsPortal");
  const { directActionsCount, portalEntries } = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  );

  useEffect(() => store.registerPortalHost(), [store]);

  if (directActionsCount > 0) return null;

  const latest = portalEntries[portalEntries.length - 1];

  return (
    <ScreenLayoutKeyboardSticky>
      <Box
        {...platformProps({ testID, slot: "screen-layout-actions-portal" })}
        accessibilityLabel="Page actions"
        className={cn(screenLayoutActionsVariants({ surface, roundedTop }), className)}
      >
        {latest?.children}
      </Box>
    </ScreenLayoutKeyboardSticky>
  );
};

ScreenLayoutActionsPortal.displayName = "ScreenLayout.ActionsPortal";

/**
 * ScreenLayout.ActionsContent — registers children into the screen's
 * store so the matching `<ActionsPortal>` host can render them. It
 * writes to the store but never subscribes, so its own writes cannot
 * re-render it. Multiple ActionsContent: last-mounted wins.
 */
export const ScreenLayoutActionsContent = ({ children }: ScreenLayoutActionsContentProps) => {
  const store = useOptionalScreenLayoutStore();
  const id = useId();

  useEffect(() => {
    store?.upsertPortalEntry(id, children);
  }, [store, id, children]);

  useEffect(() => {
    if (!store) return;
    return () => store.removePortalEntry(id);
  }, [store, id]);

  return null;
};

ScreenLayoutActionsContent.displayName = "ScreenLayout.ActionsContent";
